import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import type { AdminProject } from 'features/projects';
import { Info, MessageSquarePlus, Plus, Trash2, X } from 'lucide-react';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { useCreateScenarioMutation, useUpdateScenarioMutation } from '../../api/scenarios';
import { useGetToolCatalogQuery } from '../../api/tools';
import type { AgentTestScenario, AgentTestTurnRole } from '../../model/scenario';
import { AGENT_TEST_SCOPES, AGENT_TEST_TURN_ROLES, SCOPE_LABELS } from '../../model/scenario';
import {
  emptyScenarioFormValues,
  scenarioBodyFromFormValues,
  scenarioFormSchema,
  scenarioFormValuesFromScenario,
  type ScenarioFormValues,
} from '../../model/scenario-form';
import ScenarioCategoryField from './ScenarioCategoryField';
import ScenarioConfirmDialog from './ScenarioConfirmDialog';
import ScenarioImportDialog, { type ScenarioImportResult } from './ScenarioImportDialog';
import ScenarioToolStubsField from './ScenarioToolStubsField';
import ScenarioToolTurnFields from './ScenarioToolTurnFields';

const TURN_ROLE_LABELS: Record<AgentTestTurnRole, string> = {
  user: 'Cliente',
  assistant: 'Agente',
  tool: 'Tool',
};

const TURN_BUBBLE_LABELS: Record<AgentTestTurnRole, string> = {
  user: 'Mensaje Cliente',
  assistant: 'Mensaje Agente',
  tool: 'Contenido Tool',
};

const FIELD_HELP = {
  name: 'Nombre descriptivo del caso que se está probando.',
  description: 'Qué valida este escenario; contexto para el equipo al leer la lista.',
  scope:
    'Dónde aplica el escenario: Global corre en todas las inmobiliarias, Inmobiliaria solo en esta y Proyecto solo en el proyecto elegido.',
  subProject: 'Proyecto donde corre este escenario.',
  category: 'Obligatoria: agrupa escenarios para filtrar y correr tests por categoría; viaja como metadata a Langfuse.',
  requiredTools:
    'Solo escenarios globales: el escenario corre únicamente en inmobiliarias con todas estas tools activas. Sin selección, corre en todas.',
  turns:
    'La conversación semilla que se le entrega al agente para el test end-to-end. Necesita al menos un mensaje del cliente.',
  expectedBehavior: 'Referencia que usa el juez para puntuar la respuesta del agente en cada iteración.',
  toolStubs:
    'Respuestas simuladas por tool: cuando el agente llame una tool durante el test, se responde con este JSON en vez de ejecutarla de verdad.',
} as const;

const FieldInfo = ({ text }: { text: string }): JSX.Element => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className="inline-flex cursor-help" tabIndex={0} aria-label={text}>
        <Info className="size-3.5 text-text-muted/70" aria-hidden />
      </span>
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-[280px]">
      {text}
    </TooltipContent>
  </Tooltip>
);

const SAVE_ERROR_FALLBACK = 'No se pudo guardar el escenario. Intenta nuevamente.';

interface ScenarioFormDialogProps {
  mode: 'create' | 'edit';
  scenario: AgentTestScenario | null;
  masterProject: { id: number; name: string };
  subProjects: AdminProject[];
  categories: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValues?: ScenarioFormValues | null;
  onCreated?: (scenario: AgentTestScenario) => void;
}

const ScenarioFormDialog = ({
  mode,
  scenario,
  masterProject,
  subProjects,
  categories,
  open,
  onOpenChange,
  initialValues = null,
  onCreated,
}: ScenarioFormDialogProps): JSX.Element => {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pendingValues, setPendingValues] = useState<ScenarioFormValues | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const { data: toolCatalog } = useGetToolCatalogQuery({ projectId: masterProject.id }, { skip: !open });
  const tools = toolCatalog?.tools ?? [];

  const form = useForm<ScenarioFormValues>({
    resolver: zodResolver(scenarioFormSchema),
    defaultValues: emptyScenarioFormValues(),
  });

  const {
    fields: turnFields,
    append: appendTurn,
    remove: removeTurn,
    replace: replaceTurns,
  } = useFieldArray({
    control: form.control,
    name: 'turns',
  });

  const handleImportConversation = (result: ScenarioImportResult): void => {
    replaceTurns(
      result.turns.map((turn) => ({
        role: turn.role,
        content: turn.content,
        toolName: turn.toolName ?? '',
        toolCallId: turn.toolCallId ?? '',
      }))
    );
    if (Object.keys(result.toolStubs).length > 0) {
      let currentStubs: Record<string, unknown> = {};
      const currentText = form.getValues('toolStubsText');
      if (currentText.trim().length > 0) {
        try {
          currentStubs = JSON.parse(currentText) as Record<string, unknown>;
        } catch {
          // Invalid current JSON: replaced by the imported stubs.
        }
      }
      form.setValue('toolStubsText', JSON.stringify({ ...currentStubs, ...result.toolStubs }, null, 2), {
        shouldDirty: true,
      });
    }
  };

  const [createScenario, { isLoading: isCreating }] = useCreateScenarioMutation();
  const [updateScenario, { isLoading: isUpdating }] = useUpdateScenarioMutation();
  const isSubmitting = isCreating || isUpdating;

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && scenario) {
      form.reset(scenarioFormValuesFromScenario(scenario));
    } else {
      form.reset(initialValues ?? emptyScenarioFormValues());
    }
    setSubmitError(null);
    setPendingValues(null);
  }, [open, mode, scenario?.id, form, initialValues]);

  const watchedScope = form.watch('scope');
  const watchedTurns = form.watch('turns');
  const watchedEnabled = form.watch('enabled');

  const handleClose = (): void => {
    setSubmitError(null);
    setPendingValues(null);
    form.reset();
    onOpenChange(false);
  };

  const submitValues = async (values: ScenarioFormValues): Promise<void> => {
    setSubmitError(null);
    try {
      const body = scenarioBodyFromFormValues(values, masterProject.id);
      if (mode === 'create') {
        const created = await createScenario(body).unwrap();
        onCreated?.(created);
      } else if (scenario) {
        await updateScenario({ scenarioId: scenario.id, body }).unwrap();
      }
      handleClose();
    } catch (err) {
      setSubmitError(extractErrorMessage(err, SAVE_ERROR_FALLBACK));
    }
  };

  const onSubmit = (values: ScenarioFormValues): void => {
    const touchesGlobal = values.scope === 'global' || (mode === 'edit' && scenario?.scope === 'global');
    if (touchesGlobal) {
      setPendingValues(values);
      return;
    }
    void submitValues(values);
  };

  const handleConfirmGlobal = (): void => {
    if (!pendingValues) return;
    const values = pendingValues;
    setPendingValues(null);
    void submitValues(values);
  };

  const turnsError = form.formState.errors.turns?.root?.message ?? form.formState.errors.turns?.message ?? null;

  const title = mode === 'create' ? 'Nuevo escenario' : 'Editar escenario';
  const guardTitle = mode === 'create' ? 'Crear escenario global' : 'Guardar escenario global';
  const guardDescription =
    mode === 'create'
      ? 'Estás creando un escenario global: se ejecutará en los tests de todas las inmobiliarias que cumplan sus requisitos, no solo en la actual.'
      : 'Este cambio afecta un escenario global: impacta los tests de todas las inmobiliarias, no solo la actual.';

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => (isOpen ? onOpenChange(true) : handleClose())}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <DialogTitle>{title}</DialogTitle>
              <span className="flex items-center gap-2">
                <Switch
                  checked={watchedEnabled}
                  onCheckedChange={(checked) => form.setValue('enabled', checked, { shouldDirty: true })}
                  aria-label="Habilitado"
                />
                <span className="text-xs text-text-muted">{watchedEnabled ? 'Habilitado' : 'Deshabilitado'}</span>
              </span>
            </div>
            <DialogDescription>
              {mode === 'create' ? `Define un escenario de prueba para ${masterProject.name}.` : scenario?.name ?? ''}
            </DialogDescription>
          </DialogHeader>

          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <Form {...form}>
            <form
              onSubmit={(e) => {
                void form.handleSubmit(onSubmit)(e);
              }}
              className="flex flex-col gap-5"
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1">
                      Nombre
                      <FieldInfo text={FIELD_HELP.name} />
                    </FormLabel>
                    <FormControl>
                      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                      <Input {...field} placeholder="p. ej. Cliente pide agendar visita sin dar fecha" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1">
                      Descripción
                      <FieldInfo text={FIELD_HELP.description} />
                    </FormLabel>
                    <FormControl>
                      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                      <Textarea
                        {...field}
                        rows={2}
                        className="max-h-24"
                        placeholder="Qué debería validar este escenario…"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
                <FormField
                  control={form.control}
                  name="scope"
                  render={({ field }) => (
                    <FormItem className="flex flex-row flex-wrap items-center gap-2">
                      <FormLabel className="flex items-center gap-1">
                        Alcance:
                        <FieldInfo text={FIELD_HELP.scope} />
                      </FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="w-[150px]">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {AGENT_TEST_SCOPES.map((option) => (
                            <SelectItem key={option} value={option}>
                              {SCOPE_LABELS[option]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {watchedScope === 'subProject' ? (
                  <FormField
                    control={form.control}
                    name="subProjectId"
                    render={({ field }) => (
                      <FormItem className="flex flex-row flex-wrap items-center gap-2">
                        <FormLabel className="flex items-center gap-1">
                          Proyecto:
                          <FieldInfo text={FIELD_HELP.subProject} />
                        </FormLabel>
                        <Select
                          value={field.value !== null ? String(field.value) : undefined}
                          onValueChange={(value) => field.onChange(Number(value))}
                        >
                          <FormControl>
                            <SelectTrigger className="w-[170px]">
                              <SelectValue placeholder="Selecciona…" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {subProjects.map((sub) => (
                              <SelectItem key={sub.id} value={String(sub.id)}>
                                {sub.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : null}

                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem className="flex flex-row flex-wrap items-center gap-2">
                      <FormLabel className="flex items-center gap-1">
                        Categoría:
                        <FieldInfo text={FIELD_HELP.category} />
                      </FormLabel>
                      <FormControl>
                        <div className="w-[220px]">
                          <ScenarioCategoryField
                            value={field.value}
                            categories={categories}
                            onChange={field.onChange}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {watchedScope === 'global' ? (
                <div className="-mt-3 flex flex-col gap-2">
                  <p className="m-0 text-xs text-warning">
                    Aplicará a todas las inmobiliarias que tengan sus tools requeridas activas.
                  </p>
                  <FormField
                    control={form.control}
                    name="requiredTools"
                    render={({ field }) => (
                      <FormItem className="flex flex-row flex-wrap items-center gap-2">
                        <FormLabel className="flex items-center gap-1">
                          Tools requeridas:
                          <FieldInfo text={FIELD_HELP.requiredTools} />
                        </FormLabel>
                        <FormControl>
                          <div className="flex flex-wrap items-center gap-2">
                            <DropdownMenu modal={false}>
                              <DropdownMenuTrigger asChild>
                                <Button type="button" variant="outline" size="sm">
                                  <Plus className="size-4" aria-hidden />
                                  Agregar tool
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start" className="max-h-64 overflow-y-auto">
                                {tools.map((tool) => (
                                  <DropdownMenuCheckboxItem
                                    key={tool.name}
                                    checked={field.value.includes(tool.name)}
                                    onCheckedChange={(checked) =>
                                      field.onChange(
                                        checked
                                          ? [...field.value, tool.name]
                                          : field.value.filter((name) => name !== tool.name)
                                      )
                                    }
                                  >
                                    {tool.name}
                                  </DropdownMenuCheckboxItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            {field.value.map((name) => (
                              <Badge key={name} variant="secondary" className="gap-1">
                                {name}
                                <button
                                  type="button"
                                  className="inline-flex cursor-pointer"
                                  onClick={() => field.onChange(field.value.filter((selected) => selected !== name))}
                                  aria-label={`Quitar ${name}`}
                                >
                                  <X className="size-3" aria-hidden />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              ) : null}

              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="m-0 flex items-center gap-1 text-sm font-medium text-text-default">
                    Turnos de la conversación
                    <FieldInfo text={FIELD_HELP.turns} />
                  </p>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setImportOpen(true)}>
                      <MessageSquarePlus className="size-4" aria-hidden />
                      Importar conversación
                    </Button>
                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild>
                        <Button type="button" variant="outline" size="sm">
                          <Plus className="size-4" aria-hidden />
                          Agregar turno
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {AGENT_TEST_TURN_ROLES.map((role) => (
                          <DropdownMenuItem key={role} onSelect={() => appendTurn({ role, content: '' })}>
                            {TURN_ROLE_LABELS[role]}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                <div className="relative flex max-h-[340px] flex-col gap-3 overflow-y-auto rounded-lg border border-border-subtle p-3">
                  {turnFields.map((turnField, index) => {
                    const role = watchedTurns[index]?.role ?? 'user';
                    const isUser = role === 'user';

                    const trashButton = (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className="shrink-0 text-destructive hover:text-destructive"
                        onClick={() => removeTurn(index)}
                        disabled={turnFields.length <= 1}
                        aria-label="Eliminar turno"
                      >
                        <Trash2 className="size-4" aria-hidden />
                      </Button>
                    );

                    return (
                      <div
                        key={turnField.id}
                        className={cn('flex items-center gap-2', isUser ? 'justify-start' : 'justify-end')}
                      >
                        {!isUser ? trashButton : null}
                        <div
                          className={cn(
                            'flex w-[68%] flex-col gap-1.5 rounded-lg p-3',
                            isUser ? 'bg-bg-surface-alt' : 'bg-brand-vivid',
                            role === 'tool' && 'opacity-80'
                          )}
                        >
                          <span className={cn('text-xs font-medium', isUser ? 'text-text-muted' : 'text-white/80')}>
                            {TURN_BUBBLE_LABELS[role]}
                          </span>
                          {role === 'tool' ? <ScenarioToolTurnFields index={index} tools={tools} /> : null}
                          <FormField
                            control={form.control}
                            name={`turns.${index}.content`}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                                  <Textarea
                                    {...field}
                                    rows={2}
                                    className={cn(
                                      'max-h-40',
                                      isUser
                                        ? 'bg-bg-surface'
                                        : 'border-white/70 bg-brand-vivid text-white placeholder:text-white/60',
                                      role === 'tool' && 'font-mono text-xs'
                                    )}
                                    placeholder={role === 'tool' ? '{ "results": [] }' : 'Mensaje del turno…'}
                                  />
                                </FormControl>
                                <FormMessage className={cn(!isUser && 'text-white')} />
                              </FormItem>
                            )}
                          />
                        </div>
                        {isUser ? trashButton : null}
                      </div>
                    );
                  })}
                </div>

                {turnsError ? <p className="m-0 text-sm text-error">{turnsError}</p> : null}
              </div>

              <FormField
                control={form.control}
                name="expectedBehavior"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1">
                      Comportamiento esperado
                      <FieldInfo text={FIELD_HELP.expectedBehavior} />
                    </FormLabel>
                    <FormControl>
                      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                      <Textarea
                        {...field}
                        rows={3}
                        className="max-h-32"
                        placeholder="Qué debería hacer el agente (referencia para el judge)…"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="toolStubsText"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1">
                      Tool stubs (opcional)
                      <FieldInfo text={FIELD_HELP.toolStubs} />
                    </FormLabel>
                    <FormControl>
                      <ScenarioToolStubsField value={field.value} onChange={field.onChange} tools={tools} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {submitError ? (
                <div className="rounded-md border border-error/30 bg-error-light px-3 py-2 text-sm text-error">
                  {submitError}
                </div>
              ) : null}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Guardando…' : 'Guardar'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <ScenarioImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImport={handleImportConversation}
        projectId={masterProject.id}
        projectName={masterProject.name}
      />

      <ScenarioConfirmDialog
        open={pendingValues !== null}
        title={guardTitle}
        description={guardDescription}
        confirmLabel="Sí, continuar"
        isLoading={isSubmitting}
        onCancel={() => setPendingValues(null)}
        onConfirm={handleConfirmGlobal}
      />
    </>
  );
};

export default ScenarioFormDialog;
